import type { DiscountRefusal } from '../api/discountCode';
import type { CheckoutLanguage } from './checkoutLabels';

// Every word the checkout's discount code field prints, in each language a store may be served in.

/** The field's wording, and what each refusal reads as. */
export type DiscountCodeWords = {
  toggle: string;
  label: string;
  apply: string;
  remove: string;
  /** The totals row; `{code}` is the code that was applied. */
  row: string;
  failed: string;
  /** Under an applied code an automatic discount beats: discounts do not combine. */
  beaten: string;
  /** The notice a shared discount link brings; `{code}` is the code. */
  sharedNotice: string;
  refusals: Record<DiscountRefusal, string>;
};

/** The field in English. */
const EN: DiscountCodeWords = {
  toggle: 'Have a discount code?', label: 'Discount code', apply: 'Apply', remove: 'Remove', row: 'Discount ({code})',
  failed: 'The code could not be checked. Try again.',
  beaten: 'A bigger discount is already applied to your order.',
  sharedNotice: 'Code {code} will be applied to your order.',
  refusals: {
    NOT_FOUND: 'This code is not valid.', NOT_STARTED: 'This code is not active yet.', EXPIRED: 'This code has expired.',
    USAGE_LIMIT_REACHED: 'This code has been used up.', MINIMUM_AMOUNT_NOT_MET: 'Your order is below this code’s minimum.',
    MINIMUM_QUANTITY_NOT_MET: 'Add more items to use this code.', CART_DETAILS_REQUIRED: 'This code could not be checked for this cart.',
    PRODUCTS_NOT_ELIGIBLE: 'This code does not apply to these products.', ALREADY_REDEEMED: 'You have already used this code.'
  }
};

/** The field in French. */
const FR: DiscountCodeWords = {
  toggle: 'Vous avez un code promo ?', label: 'Code promo', apply: 'Appliquer', remove: 'Retirer', row: 'Réduction ({code})',
  failed: 'Le code n’a pas pu être vérifié. Réessayez.',
  beaten: 'Une réduction plus avantageuse est déjà appliquée à votre commande.',
  sharedNotice: 'Le code {code} sera appliqué à votre commande.',
  refusals: {
    NOT_FOUND: 'Ce code n’est pas valide.', NOT_STARTED: 'Ce code n’est pas encore actif.', EXPIRED: 'Ce code a expiré.',
    USAGE_LIMIT_REACHED: 'Ce code a atteint sa limite d’utilisation.', MINIMUM_AMOUNT_NOT_MET: 'Votre commande n’atteint pas le minimum de ce code.',
    MINIMUM_QUANTITY_NOT_MET: 'Ajoutez des articles pour utiliser ce code.', CART_DETAILS_REQUIRED: 'Ce code n’a pas pu être vérifié pour ce panier.',
    PRODUCTS_NOT_ELIGIBLE: 'Ce code ne s’applique pas à ces produits.', ALREADY_REDEEMED: 'Vous avez déjà utilisé ce code.'
  }
};

/** The field in Arabic. */
const AR: DiscountCodeWords = {
  toggle: 'لديك رمز تخفيض؟', label: 'رمز التخفيض', apply: 'تطبيق', remove: 'إزالة', row: 'تخفيض ({code})',
  failed: 'تعذر التحقق من الرمز. حاول من جديد.',
  beaten: 'تم تطبيق تخفيض أفضل على طلبك.',
  sharedNotice: 'سيتم تطبيق الرمز {code} على طلبك.',
  refusals: {
    NOT_FOUND: 'هذا الرمز غير صالح.', NOT_STARTED: 'هذا الرمز غير مفعل بعد.', EXPIRED: 'انتهت صلاحية هذا الرمز.',
    USAGE_LIMIT_REACHED: 'استُنفد هذا الرمز.', MINIMUM_AMOUNT_NOT_MET: 'طلبك أقل من الحد الأدنى لهذا الرمز.',
    MINIMUM_QUANTITY_NOT_MET: 'أضف قطعا أخرى لاستعمال هذا الرمز.', CART_DETAILS_REQUIRED: 'تعذر التحقق من هذا الرمز لهذه السلة.',
    PRODUCTS_NOT_ELIGIBLE: 'هذا الرمز لا يشمل هذه المنتجات.', ALREADY_REDEEMED: 'لقد استعملت هذا الرمز من قبل.'
  }
};

/** Every language's field. */
export const DISCOUNT_CODE_WORDS: Readonly<Record<CheckoutLanguage, DiscountCodeWords>> = { en: EN, fr: FR, ar: AR };
